// The ASCII moon (DESIGN.md: ascii-stage), as pure math so the page
// component and the social image renderer draw the same thing. A cratered
// sphere seen by a camera that orbits it: the sunlight stays fixed to the
// moon, so the light moves with the surface and the phase changes as the
// camera goes round. Plus a few twinkling stars.

export const COLS = 52;
export const ROWS = 25; // character cells are about twice as tall as wide
export const STEP = (Math.PI * 2) / 240; // one orbit every 240 frames
export const START = 0.6;
/** Star twinkle phase advanced per tick. */
export const TWINKLE_RATE = 0.15;

/**
 * Cell colors: "hi" on-surface (the brightest moon characters), "" muted,
 * "lo" the control color (dim), "star" primary (a twinkling star).
 */
export type Cell = { ch: string; cls: "" | "hi" | "lo" | "star" };

const RAMP = " .:-=+*#%@";

export const unit = (x: number, y: number, z: number) => {
  const l = Math.hypot(x, y, z);
  return [x / l, y / l, z / l] as const;
};
const LIGHT = unit(-0.6, -0.35, 0.72);

function fromLonLat(lon: number, lat: number) {
  return [Math.cos(lat) * Math.sin(lon), Math.sin(lat), Math.cos(lat) * Math.cos(lon)] as const;
}

// Craters and darker maria as [longitude, latitude, radius] in radians.
const CRATERS = [
  [0.3, -0.35, 0.22], [1.2, 0.25, 0.16], [2.1, -0.1, 0.26], [2.9, 0.45, 0.13],
  [3.8, -0.4, 0.18], [4.6, 0.1, 0.21], [5.5, -0.2, 0.12], [0.9, 0.6, 0.1],
  [-0.4, 0.15, 0.09], [3.3, -0.05, 0.08],
].map(([lon, lat, r]) => ({ v: fromLonLat(lon!, lat!), r: r! }));
const MARIA = [
  [0.7, -0.05, 0.55], [2.6, 0.2, 0.45], [4.9, -0.3, 0.5],
].map(([lon, lat, r]) => ({ v: fromLonLat(lon!, lat!), r: r! }));

// Fixed background stars: [col, row, phase].
const STARS: [number, number, number][] = [
  [3, 2, 0], [47, 1, 1.7], [50, 9, 3.1], [1, 14, 4.4], [6, 22, 2.2], [45, 21, 5.3], [49, 16, 0.9], [8, 7, 3.8],
];

const R2 = 0.88; // disc radius squared, in cell-normalized units

type Vec = readonly [number, number, number];

/**
 * One frame with the camera at `angle`. `light` is where the sun appears from
 * the camera's starting point (START); it defaults to upper left, a gibbous
 * moon. The sun is fixed to the moon, so it turns with the surface.
 */
export function moonCells(angle: number, tick: number, light: Vec = LIGHT): Cell[][] {
  const ca = Math.cos(angle);
  const sa = Math.sin(angle);
  // The sun in the moon's own frame.
  const sun = [
    light[0] * Math.cos(START) + light[2] * Math.sin(START),
    light[1],
    -light[0] * Math.sin(START) + light[2] * Math.cos(START),
  ] as const;
  const stars = new Map<number, Cell>();
  for (const [c, r, phase] of STARS) {
    const s = Math.sin(tick * TWINKLE_RATE + phase);
    stars.set(r * COLS + c, s > 0.75 ? { ch: "+", cls: "star" } : s > -0.2 ? { ch: ".", cls: "lo" } : { ch: " ", cls: "" });
  }

  const rows: Cell[][] = [];
  for (let r = 0; r < ROWS; r++) {
    const row: Cell[] = [];
    for (let c = 0; c < COLS; c++) {
      const nx = ((c + 0.5) / COLS) * 2 - 1;
      const ny = ((r + 0.5) / ROWS) * 2 - 1;
      if ((nx * nx + ny * ny) / R2 >= 1) {
        row.push(stars.get(r * COLS + c) ?? { ch: " ", cls: "" });
        continue;
      }
      const x = nx / Math.sqrt(R2);
      const y = ny / Math.sqrt(R2);
      const z = Math.sqrt(Math.max(0, 1 - x * x - y * y));
      // Surface point in the moon's own frame.
      const ox = x * ca + z * sa;
      const oz = -x * sa + z * ca;
      let albedo = 1;
      for (const m of MARIA) {
        const a = Math.acos(Math.min(1, ox * m.v[0] + y * m.v[1] + oz * m.v[2]));
        if (a < m.r) albedo -= 0.28 * (1 - a / m.r);
      }
      for (const k of CRATERS) {
        const a = Math.acos(Math.min(1, ox * k.v[0] + y * k.v[1] + oz * k.v[2]));
        if (a < k.r) albedo *= 0.55;
        else if (a < k.r * 1.3) albedo *= 1.18;
      }
      const lambert = Math.max(0, ox * sun[0] + y * sun[1] + oz * sun[2]);
      // A little earthshine keeps the night side faintly visible.
      const b = Math.min(1, albedo * (0.12 + 0.85 * lambert));
      const i = Math.min(RAMP.length - 1, Math.floor(b * RAMP.length));
      row.push({ ch: RAMP[i]!, cls: i >= 8 ? "hi" : i <= 2 ? "lo" : "" });
    }
    rows.push(row);
  }
  return rows;
}

/** One frame as HTML for a <pre>: runs of the same class share a span. */
export function moonHtml(angle: number, tick: number): string {
  let html = "";
  for (const row of moonCells(angle, tick)) {
    let run = "";
    let cls = "";
    const flush = () => {
      if (run) html += cls ? `<span class="${cls}">${run}</span>` : run;
      run = "";
    };
    for (const cell of row) {
      if (cell.cls !== cls) {
        flush();
        cls = cell.cls;
      }
      run += cell.ch;
    }
    flush();
    html += "\n";
  }
  return html;
}
